import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import BatchPagination from "../../batch/BatchPagination";
import { numOfPage, PackageAssignmentAutomatedColumns, PackageAssignmentManualColumns } from "../../../constant";
import moment from "moment";
import LoaderSpinner from "../../common/Loader";
import _, { set } from "lodash";
import { toast } from "react-toastify";
import PackageAssignmentFilter from "./PackageAssignmentFilter";
import BulkApprovalModal from "./BulkApprovalModal";
import "./PackageAssignmentApproval.css";
import {
  getPackageAssignmentList,
  handleApproveRequest
} from "../../../store/actions/packageAssignment";
import DataTable from "../../common/DataTable";
import SearchProduct from "../../Inventory/Product/searchProduct";
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from "react-router-dom";
import ViewImage from "../../common/ViewImage";
import { packageApprovalBackDetails } from "../../../store/actions/incoming";

const PackageAssignmentApproval = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userDetails = useSelector((state) => state.auth.user);
  const packageApprovalBackDetailsObj = useSelector((state) => state?.IncomingReducer?.packageApprovalBackDetailsObj);
  const {packageAssignmentPageInfo, packageAssignmentList, packageAssignmentStatus} = useSelector((state) => state.packageAssignmentReducer);

  // Component state
  const [data, setData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showBulkApprovalModal, setShowBulkApprovalModal] = useState(false);
  const [imagePreviewRecord, setImagePreviewRecord] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);

  useEffect(() => {
    createListingData();
  }, [packageAssignmentList]);

  const createListingData = () => {
    if (packageAssignmentList?.length) {
      const listingData = packageAssignmentList.map((item, index) => ({
        ...item,
        listIndex: index,
        id: uuidv4(),
      }));
      setData(listingData);
    } else {
      setData([]);
    }
  };

  // Default filter data
  const defaultFilterData = useMemo(() => ({
    pageIndex: 1,
    pageSize: 30,
    sortColumn: "",
    sortOrder: 'DESCENDING',
    searchKeywords: "",
    trackingNumber: "", // "", "Automated", "Manual"
    packageTypes: [], // "", "Pending", "Approved", "Rejected"
    sources: [],
    sellerIdList: [],
    companyIdList: [],
    fromDate: moment().subtract(30, 'days').toDate(),
    toDate: moment().toDate(),
    carriers: [],
    action: 1,
  }), []);

  const [filterData, setFilterData] = useState({});

  // Calculate total pages
  useEffect(() => {
    if (packageAssignmentList?.length) {
      setTotalPages(
        Math.ceil(packageAssignmentPageInfo?.TotalRecords / filterData.pageSize)
      );
    }
  }, [packageAssignmentList, packageAssignmentPageInfo, filterData.pageSize]);

  // Initialize filter data from back details if available
  useEffect(() => {
    if (_.isEmpty(packageApprovalBackDetailsObj)) {
      setFilterData(defaultFilterData);
    } else {
      setFilterData({...packageApprovalBackDetailsObj });
    }
  }, [packageApprovalBackDetailsObj]);

  // Fetch data when filters change
  useEffect(() => {
    if (!_.isEmpty(filterData)) {
      fetchPackageAssignmentList();
    }
  }, [
    filterData.pageIndex,
    filterData.pageSize,
    filterData.sortColumn,
    filterData.sortOrder,
    filterData.action
  ]);

  // Handle loader state
  useEffect(() => {
    setLoader(packageAssignmentStatus === "PENDING");
  }, [packageAssignmentStatus]);

  // Check if user is super admin
  const isSuperAdmin = userDetails?.Value?.isSuperAdmin;

  const fetchPackageAssignmentList = () => {
    const apiData = {
      ...filterData,
      sources: _.map(filterData.sources, 'id') || [],
      companyIdList: _.map(filterData.companyIdList, "Id"),
      sellerIdList: _.map(filterData.sellerIdList, "id"),
      fromDate: filterData.fromDate ? moment(filterData.fromDate).format('YYYY-MM-DDT00:00:00') : null,
      toDate: filterData.toDate ? moment(filterData.toDate).format('YYYY-MM-DDT00:00:00') : null,
      assignmentType: "",
    };
    
    dispatch(getPackageAssignmentList(apiData));
  };

  // Handle sorting
  const handleSortingColumn = (sortColumn) => {
    const newSortOrder = filterData.sortOrder === "ASCENDING" ? "DESCENDING" : "ASCENDING";
    setFilterData({
      ...filterData,
      sortColumn: sortColumn,
      sortOrder: newSortOrder,
      pageIndex: 1
    });
  };

  // Handle search
  const handleSearchFilter = (e, type) => {
    if (type === "clear") {
      handleResetSearch();
      return;
    }
    if (filterData.pageIndex !== 1) {
      setFilterData({ ...filterData, pageIndex: 1, searchKeywords: filterData.searchKeywords });
    } else {
      fetchPackageAssignmentList();
    }
  };

  const handleResetSearch = () => {
    const resetData = {
      ...filterData,
      pageIndex: 1,
      searchKeywords: ''
    };
    if (filterData.pageIndex !== 1) {
      setFilterData(resetData);
    } else {
      const payloadData = {
        ...filterData,
        sources: _.map(filterData.sources, 'id') || [],
        companyIdList: _.map(filterData.companyIdList, "Id"),
        sellerIdList: _.map(filterData.sellerIdList, "id"),
        fromDate: filterData.fromDate ? moment(filterData.fromDate).format('YYYY-MM-DDT00:00:00') : null,
        toDate: filterData.toDate ? moment(filterData.toDate).format('YYYY-MM-DDT00:00:00') : null,
        assignmentType: "",
        searchKeywords: ''
      };
      dispatch(getPackageAssignmentList(payloadData));
      setFilterData({
        ...filterData,
        searchKeywords: ''
      });
    }
  };

  // Handle reset filter
  const handleReset = () => {
    const resetData = {
      ...defaultFilterData,
      showFilter: false,
      pageIndex: 1
    };
    setFilterData(resetData);
    if (filterData.pageIndex !== 1) {
      setFilterData(resetData);
    } else {
      dispatch(getPackageAssignmentList(resetData));
    }
  };

  // Handle bulk approve/reject
  const handleBulkApproveReject = (action) => {
    if (selectedItems.length === 0) {
      toast.warning("Please select at least one item");
      return;
    }
    setShowBulkApprovalModal(true);
  };

  // Handle bulk approval confirmation
  const handleBulkApprovalConfirm = async () => {
    try {
      setLoader(true);
      let data = {
        accountTransactionIds: selectedItems.map(item => item.AccountTransactionId),
      };
      let response = await handleApproveRequest(data);
      
      if (response.data.Status === 200) {
        if (selectedItems.length === 1) {
          toast.success(`Package assignment approved successfully`);
        } else {
          toast.success(`${selectedItems.length} package assignments approved successfully`);
        }
        setSelectedItems([]);
        fetchPackageAssignmentList();
      } else {
        toast.error(response.message || `Failed to approve package assignments`);
      }
    } catch (error) {
      toast.error(`Error approving package assignments`);
    } finally {
      setLoader(false);
      setShowBulkApprovalModal(false);
    }
  };

  // Check if user has permission to access this module
  if (!isSuperAdmin) {
    return (
      <div className="main__container">
        <div className="alert alert-danger">
          Access Denied: This module is only available for Super Administrators.
        </div>
      </div>
    );
  }

  const handleTabChange = (tab) => {
    setFilterData({
      ...filterData,
      action: tab === 'automated' ? 1 : 2,
      pageIndex: 1
    });
    setSelectedItems([]);
  };

  const handleEdit = (row, type) => {
    // Save current state for navigation back to approval screen
    const stateToSave = {
        ...filterData,
        searchKeyword: filterData.searchKeyword || '',
        isApprovalScreen: true // Flag to identify this is from approval screen
    };
    dispatch(packageApprovalBackDetails(filterData));
    // Navigate to carrier connect edit page
    navigate(`/incoming-packages/fd-${row.receivedDate}-3pl/${row.PackageId}`, {
        state: {
            data: row,
            isEdit: type === 'edit' ? true : false,
            view: type === 'edit' ? false : true,
            searchState: stateToSave,
            fromApprovalScreen: true // Flag to help return to approval screen
        }
    });
  };

  const handleApprove = async (item) => {
    let payload = {
      accountTransactionIds: [item.AccountTransactionId],
    };
    let data = payload;
    let response = await handleApproveRequest(data);
    if (response?.data?.Status === 200) {
        toast.success(response.data.Entity.message || `Package assignment approved successfully`);
        fetchPackageAssignmentList();
        setSelectedItems([]);
    } else {
        toast.error(response.message || `Failed to approve package assignment`);
    }
  };

  const handleAction = (item, type, actionObj = {}) => {
      if (type === 'approve') {
          handleApprove(item);
          return;
      }
      if (type === 'thumbnail') {
          handlePreviewImage(item);
          return;
      }
      if (type === 'edit') {
          handleEdit(item, 'edit');
          return;
      }
      if (type === 'view') {
          handleEdit(item, 'view');
          return;
      }
  };

  const handleCheckRecord = (e, type) => {
    const { checked } = e.target;
    let recordData = [...data];
    if (_.isEmpty(type)) {
      let markCheck = recordData.map((item) => ({
          ...item,
          isChecked: checked,
      }));
      setData(markCheck);
      let findChecked = markCheck.filter(item => item.isChecked);
      setSelectedItems(findChecked);
    } else {
      recordData[type.listIndex].isChecked = checked;
      setData(recordData);
      if (!checked) {
          let filteredData = selectedItems.filter(
              (item) => item.id !== recordData[type.listIndex].id
          );
          setSelectedItems(filteredData);
          return;
      } else {
          setSelectedItems([...selectedItems, recordData[type.listIndex]]);
      }
    }
  };

  const handlePreviewImage = (item) => {
    setImagePreviewRecord(item);
    setShowImageModal(true);
  };

  const handleSearchText = (text) => {
    setFilterData({ ...filterData, searchKeywords: text });
  };


  return (
    <main className="package-assignment-approval">
      <div className="main__container">
        <div className="main__tile_heading cus-btn-include-sec">
          <span>Package Assignment Approval</span>
          <div className="top_right_button_container">
            {filterData.action === 1 && selectedItems.length > 0 && (
              <>
                <button
                  className="btn btn-success top-btn m2"
                  onClick={() => handleBulkApproveReject('approve')}
                  disabled={loader}
                >
                  Bulk Approve ({selectedItems.length})
                </button>
              </>
            )}
          </div>
        </div>
        {loader && (<div className="full-screen-spinner-overlay"><LoaderSpinner /></div>)}
        
        <div className="show searchBox mb-2">
          <SearchProduct
            showSearch={true}
            searchTxt={filterData.searchKeywords}
            setSearchTxt={handleSearchText}
            handleReset={handleSearchFilter}
            handleSearchFilter={handleSearchFilter}
            placeHolderText="Search Company, Seller, Seller Code, Tracking Number, Package Type, Carrier, STE Code or Source"
          />
        </div>

        {/* Filter Component */}
        <div className="zIndex2">
          <PackageAssignmentFilter
            filterData={filterData}
            setFilterData={setFilterData}
            getPackageAssignmentList={fetchPackageAssignmentList}
            defaultFilterData={defaultFilterData}
            handleReset={handleReset}
          />
        </div>

        {/* Tab Navigation */}
        <div className="package-assignment-tabs">
          <nav className="nav nav-tabs">
            <button
              className={`nav-link ${filterData.action === 1 ? 'active' : ''}`}
              onClick={() => handleTabChange('automated')}
            >
              Automated
            </button>
            <button
              className={`nav-link ${filterData.action === 2 ? 'active' : ''}`}
              onClick={() => handleTabChange('manual')}
            >
              Manual
            </button>
          </nav>
        </div>

        {/* Main Content */}
        <div className="main_content cus-page-content-sec zIndex1">
          <div className="main_content_table_history">
            <div className="incomingTbl tableFixHead">
              <DataTable 
                tableData={data || []}
                headers={filterData.action === 1 ? PackageAssignmentAutomatedColumns : PackageAssignmentManualColumns}   
                handleSortingColumn={handleSortingColumn}
                tableDataStatus={packageAssignmentStatus}
                filterData={filterData}
                handleAction={handleAction}
                handleCheckRecord={handleCheckRecord}
                isSelectAll={data?.filter(item => item.isChecked).length === data.length}
                actionType={filterData.action === 1 ? 11 : 3}
              />
            </div>
          </div>

          {/* Pagination */}
          <BatchPagination
            gridData={packageAssignmentList || []}
            totalVisualRecords={packageAssignmentList?.length}
            numberOfPage={filterData.pageSize}
            numOfPage={numOfPage}
            loader={loader}
            currentPage={filterData.pageIndex}
            totalPages={totalPages || 0}
            totalRecords={packageAssignmentPageInfo?.TotalRecords}
            setCurrentPage={(value) => setFilterData({ ...filterData, pageIndex: value })}
            setNumberOfPage={(value) => setFilterData({ ...filterData, pageSize: value, pageIndex: 1 })}
          />
        </div>

        {/* Bulk Approval Modal */}
        <BulkApprovalModal
          show={showBulkApprovalModal}
          onHide={() => setShowBulkApprovalModal(false)}
          onConfirm={handleBulkApprovalConfirm}
          selectedItems={selectedItems}
          loader={loader}
        />

        {showImageModal && (
          <ViewImage
              selectedRecord={imagePreviewRecord}
              showHideImageModal={showImageModal}
              setShowHideImageModal={setShowImageModal}
              title="Preview"
          />
        )}
      </div>
    </main>
  );
};

export default PackageAssignmentApproval;